/**
 * Per-tenant theme application. Sets CSS custom properties on the document
 * root so every DOM overlay that binds `var(--sg-...)` picks up the palette.
 *
 * Three.js scenes can't consume CSS vars directly; they read
 * {@link getThemeVar} at scene init to grab a parsed hex value. The theme
 * is not expected to live-update inside a running scene — the streamer
 * sets it once per game.
 *
 * @packageDocumentation
 */
import type { ThemePalette } from '@sg/shared';

/** CSS custom-property names written by {@link applyTheme}. */
export const THEME_VARS = {
  primary: '--sg-primary',
  secondary: '--sg-secondary',
  accent: '--sg-accent',
  background: '--sg-bg',
  text_on_primary: '--sg-text-on-primary',
  text_on_secondary: '--sg-text-on-secondary',
  tile_bg: '--sg-tile-bg',
  celebration: '--sg-celebration',
} as const;

/**
 * Default palette. Matches the hardcoded hex values that shipped before the
 * theme system existed, so clients that never receive a themed snapshot
 * render identically to the pre-theme build.
 */
export const DEFAULT_THEME: ThemePalette = {
  primary: '#FFC43D',
  secondary: '#060CE9',
  accent: '#00F0FF',
  background: '#0A0E27',
  text_on_primary: '#060CE9',
  text_on_secondary: '#FFC43D',
  tile_bg: '#060CE9',
  celebration: '#22C55E',
};

/**
 * Write every palette slot to `document.documentElement` as a CSS custom
 * property. Safe to call repeatedly; the browser handles no-op writes.
 * No-op when `document` is undefined (test / node env).
 */
export function applyTheme(theme: ThemePalette): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty(THEME_VARS.primary, theme.primary);
  root.style.setProperty(THEME_VARS.secondary, theme.secondary);
  root.style.setProperty(THEME_VARS.accent, theme.accent);
  root.style.setProperty(THEME_VARS.background, theme.background);
  root.style.setProperty(THEME_VARS.text_on_primary, theme.text_on_primary);
  root.style.setProperty(THEME_VARS.text_on_secondary, theme.text_on_secondary);
  root.style.setProperty(THEME_VARS.tile_bg, theme.tile_bg);
  root.style.setProperty(THEME_VARS.celebration, theme.celebration);
}

/**
 * Read a theme CSS custom property as a `#RRGGBB` string. Used by Three.js
 * scenes that can't consume CSS vars directly (e.g. `new Color(hex)`). Falls
 * back to `fallback` when the property is unset or malformed.
 */
export function getThemeVar(
  name: keyof typeof THEME_VARS,
  fallback: string,
): string {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') {
    return fallback;
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(THEME_VARS[name])
    .trim();
  return /^#[0-9A-Fa-f]{6}$/.test(raw) ? raw : fallback;
}
